import {
  useDisconnectAccountFromFornax,
  useRequestConnectAccountToFornax,
} from '@/api/mutations/integrations/fornax';
import { Button, Modal, useToastContext } from '@payaca/ambrosia';

import useGetMyAccountIntegrations from '@/api/queries/me/useGetMyAccountIntegrations';
import { useSelector } from '@/api/state';
import {
  ConnectionControl,
  ConnectionControlBenefitList,
} from '@/ui/components';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';

import { FC, useMemo, useState } from 'react';
import { getUserRoles } from '../../../../utils/stateAccessors';

import fornaxLogo from './fornax.jpeg';

export const FornaxConnectionControl: FC = () => {
  const [isSettingUpConnection, setIsSettingUpConnection] = useState(false);

  const { mutateAsync: requestConnect } = useRequestConnectAccountToFornax();
  const { mutateAsync: disconnect } = useDisconnectAccountFromFornax();
  const { pushSuccess, pushError } = useToastContext();

  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetMyAccountIntegrations();

  const hasConnection = integrations.some((i) => i.service === 'FORNAX');

  const userRoles = useSelector(getUserRoles);
  const isLoading = isLoadingIntegrations;

  const canUpdate = useMemo(() => {
    return userHasRequiredPermission(userRoles, [
      AccountsPermissions.UPDATE_CONNECTION,
    ]);
  }, [userRoles]);

  const handleConnect = async () => {
    try {
      await requestConnect();

      pushSuccess(
        'Your request has been submitted. Our team will reach out to help complete your Fornax connection.'
      );

      setIsSettingUpConnection(false);
    } catch (err: unknown) {
      pushError(
        'We were unable to submit your request. Please try again later.'
      );
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect();

      pushSuccess('Your Fornax connection has been removed.');
    } catch (err: unknown) {
      pushError('Unable to disconnect from Fornax.');
    }
  };

  return (
    <ConnectionControl
      disableConnection={!canUpdate}
      allowConnectDisconnect={canUpdate && !isLoading}
      connectionName="Fornax"
      isConnected={hasConnection}
      onAddConnection={() => setIsSettingUpConnection(true)}
      onRemoveConnection={handleDisconnect}
      logo={fornaxLogo}
    >
      {hasConnection ? (
        <p>Your Fornax connection is active.</p>
      ) : (
        <>
          Connect Payaca to Fornax to access a range of financial and compliance
          services that help you win business, save time, and manage your
          cashflow. Fornax offers:
          <ConnectionControlBenefitList
            benefits={[
              'Specialist affordable heat pump financing and care plans for consumers, each one delivering recurring revenue for you.',
              'A free MCS umbrella and rapid capital advances for procurement on your projects.',
            ]}
          />
        </>
      )}

      <Modal
        isOpen={isSettingUpConnection}
        onClose={() => setIsSettingUpConnection(false)}
        title="Connect your Fornax account"
      >
        <Modal.Body>
          <p className="mb-4">
            A member of our team will assist you with completing the Fornax
            integration. Click below to submit your request.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button onClick={handleConnect}>Submit Request</Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </ConnectionControl>
  );
};
